import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ProductService, { ModuleConfigRow } from '../../../services/product.service';
import Toast from '../../../components/Toast';

interface ProductModuleVisibilityProps {
  productId: string;
}

const ALWAYS_ON_MODULE_LABELS: Record<string, string> = {
  tenants: 'Tenants',
  provisioning: 'Provisioning',
  config: 'Config',
  masters: 'Masters',
  iam: 'IAM',
  products: 'Products',
  subscriptions: 'Subscriptions',
  audit: 'Audit',
  global_settings: 'Global Settings',
};

const TOGGLEABLE_MODULE_LABELS: Record<string, string> = {
  feature_flags: 'Feature Flags',
  branding_override: 'Branding Override',
  impersonation: 'Impersonation',
  compliance: 'Compliance & Governance',
  data_management: 'Data Management — Export & Retention',
  notifications_outbox: 'Notifications Outbox',
  reports: 'Reports',
};

const ProductModuleVisibility: React.FC<ProductModuleVisibilityProps> = ({ productId }) => {
  const { t } = useTranslation();
  const [toggleable, setToggleable] = useState<ModuleConfigRow[]>([]);
  const [alwaysOn, setAlwaysOn] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    ProductService.getModuleConfig(productId)
      .then((res: any) => {
        setToggleable(res?.data?.toggleable ?? []);
        setAlwaysOn(res?.data?.alwaysOn ?? []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const handleToggle = async (moduleKey: string, nextEnabled: boolean) => {
    setSavingKey(moduleKey);
    const previous = toggleable;
    setToggleable((rows) => rows.map((r) => (r.module_key === moduleKey ? { ...r, is_enabled: nextEnabled } : r)));
    try {
      await ProductService.updateModuleConfig(productId, moduleKey, nextEnabled);
      toast.success(<Toast message={t('masters.products.moduleVisibility.saved')} />, { icon: false });
    } catch {
      setToggleable(previous);
      toast.error(<Toast message={t('masters.products.moduleVisibility.saveError')} />, { icon: false });
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border spinner-border-sm text-primary" />
      </div>
    );
  }

  return (
    <div className="card mt-6" style={{ borderRadius: '0.75rem' }}>
      <div className="card-header border-bottom pb-3">
        <h6 className="mb-0 fw-semibold">{t('masters.products.moduleVisibility.title')}</h6>
        <p className="text-muted small mb-0 mt-1">{t('masters.products.moduleVisibility.subtitle')}</p>
      </div>
      <div className="card-body">
        <div className="d-flex flex-column gap-3">
          {toggleable.map((row) => (
            <div
              key={row.module_key}
              className="d-flex align-items-center justify-content-between py-2 border-bottom"
            >
              <div className="small fw-medium">
                {TOGGLEABLE_MODULE_LABELS[row.module_key] ?? row.module_key}
              </div>
              <div className="form-check form-switch mb-0 ms-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={row.is_enabled}
                  disabled={savingKey === row.module_key}
                  onChange={() => handleToggle(row.module_key, !row.is_enabled)}
                  style={{ cursor: 'pointer', width: '2.5em', height: '1.25em' }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <div className="text-muted small mb-2">{t('masters.products.moduleVisibility.alwaysOnLabel')}</div>
          <div className="d-flex flex-column gap-2">
            {alwaysOn.map((key) => (
              <div key={key} className="d-flex align-items-center justify-content-between py-1 opacity-50">
                <div className="small">
                  <i className="ri-lock-2-line me-2"></i>
                  {ALWAYS_ON_MODULE_LABELS[key] ?? key}
                </div>
                <div className="form-check form-switch mb-0 ms-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked
                    disabled
                    style={{ width: '2.5em', height: '1.25em' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModuleVisibility;
